/**
 * OperationalRequirements Component
 * Displays post-construction waste services guidance including container requirements
 * and enclosure specifications with emphasis on 180° gate swing for driver safety.
 *
 * Based on SB 1383 requirements and industry best practices.
 */

import type { OperationalRequirements, ContainerSpec } from '@calwmp/shared';

export interface OperationalRequirementsProps {
  requirements?: OperationalRequirements | null;
  projectName?: string;
  projectType?: string;
  squareFootage?: number;
  onContactHauler?: () => void;
}

/**
 * Format container display (e.g., "2 × 4-yard containers")
 */
function formatContainerDisplay(spec: ContainerSpec): string {
  const plural = spec.count !== 1 ? 's' : '';
  return `${spec.count} × ${spec.size}-yard container${plural}`;
}

/**
 * OperationalRequirements Component
 *
 * Displays operational guidance for post-construction waste services with:
 * - Container requirements (trash, recycling, organics)
 * - Enclosure specifications with 180° gate swing emphasis
 * - Accessibility requirements
 * - "Why This Matters" educational section
 * - Connect with hauler CTA
 *
 * Gracefully handles missing data by not rendering when requirements are null/undefined.
 */
export function OperationalRequirements({
  requirements,
  projectName = 'your project',
  projectType,
  squareFootage,
  onContactHauler,
}: OperationalRequirementsProps) {
  // Don't render if no requirements data
  if (!requirements) {
    return null;
  }

  const { containers, enclosure, accessibility } = requirements;

  const handleContactHauler = () => {
    if (onContactHauler) {
      onContactHauler();
    } else {
      // Default mailto for MVP
      const subject = encodeURIComponent(`Waste Services Inquiry for ${projectName}`);
      const body = encodeURIComponent(
        `Hello,\n\nI'm planning waste services for ${projectName} and would like to discuss:\n\n` +
        `Container Requirements:\n` +
        `- Trash: ${formatContainerDisplay(containers.trash)}\n` +
        `- Recycling: ${formatContainerDisplay(containers.recycling)}\n` +
        `- Organics: ${formatContainerDisplay(containers.organics)}\n\n` +
        `Please contact me to discuss service options and pricing.\n\nThank you!`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }
  };

  return (
    <div className="card mb-6" data-testid="operational-requirements">
      {/* Header */}
      <div className="border-b border-neutral-200 pb-4 mb-6">
        <h2 className="text-xl font-semibold text-neutral-900 mb-2">
          Post-Construction Waste Services
        </h2>
        <p className="text-neutral-600 text-sm">
          Operational guidance to help you plan for compliant waste management after construction
        </p>
      </div>

      {/* Container Requirements Section */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Container Requirements</h3>
        {(projectType || squareFootage) && (
          <p className="text-sm font-medium text-neutral-700 mb-2">
            Based on: {projectType}
            {squareFootage && `, ${squareFootage.toLocaleString()} sq ft`}
          </p>
        )}
        <p className="text-sm text-neutral-600 mb-4">
          {`You'll need the following containers for 3-stream collection:`}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Trash Container */}
          <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200">
            <div className="flex items-center mb-2">
              <svg
                className="w-5 h-5 text-neutral-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <h4 className="font-semibold text-neutral-900">Trash</h4>
            </div>
            <p className="text-2xl font-bold text-neutral-900 mb-1">
              {formatContainerDisplay(containers.trash)}
            </p>
            <p className="text-xs text-neutral-600">Landfill-bound materials</p>
          </div>

          {/* Recycling Container */}
          <div className="p-4 bg-accent-50 rounded-lg border border-accent-200">
            <div className="flex items-center mb-2">
              <svg
                className="w-5 h-5 text-accent-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <h4 className="font-semibold text-accent-900">Recycling</h4>
            </div>
            <p className="text-2xl font-bold text-accent-900 mb-1">
              {formatContainerDisplay(containers.recycling)}
            </p>
            <p className="text-xs text-accent-700">Recyclable materials</p>
          </div>

          {/* Organics Container */}
          <div className="p-4 bg-accent-50 rounded-lg border border-accent-200">
            <div className="flex items-center mb-2">
              <svg
                className="w-5 h-5 text-accent-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h4 className="font-semibold text-accent-900">Organics</h4>
            </div>
            <p className="text-2xl font-bold text-accent-900 mb-1">
              {formatContainerDisplay(containers.organics)}
            </p>
            <p className="text-xs text-accent-700">Food and yard waste</p>
          </div>
        </div>
      </div>

      {/* Enclosure Specifications Section */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Enclosure Specifications</h3>

        {/* Minimum Dimensions */}
        <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200 mb-4">
          <h4 className="font-semibold text-neutral-900 mb-3">Minimum Dimensions</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-neutral-900">{`${enclosure.minWidth}'`}</div>
              <div className="text-xs text-neutral-600">Width</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900">{`${enclosure.minDepth}'`}</div>
              <div className="text-xs text-neutral-600">Depth</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900">{`${enclosure.minHeight}'`}</div>
              <div className="text-xs text-neutral-600">Height</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-neutral-900">{`${enclosure.gateWidth}'`}</div>
              <div className="text-xs text-neutral-600">Gate Width</div>
            </div>
          </div>
        </div>

        {/* Critical Design Features */}
        <div className="space-y-3">
          <h4 className="font-semibold text-neutral-900">Critical Design Features</h4>

          {/* 180° Gate Swing - EMPHASIZED */}
          <div className="p-4 bg-orange-50 border-2 border-orange-300 rounded-lg">
            <div className="flex items-start">
              <svg
                className="w-5 h-5 text-orange-600 mr-3 mt-0.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div className="flex-1">
                <div className="font-semibold text-orange-900 mb-1">
                  <strong>180° gate swing</strong> (full swing-back for driver safety)
                </div>
                <p className="text-sm text-orange-800">
                  Gates must open fully backward, not just 90°. This critical ergonomic detail prevents
                  driver injuries and damage by allowing safe container access without gates catching on doors.
                </p>
              </div>
            </div>
          </div>

          {/* Other Requirements */}
          <div className="space-y-2">
            <div className="flex items-center text-sm">
              <svg
                className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-neutral-900">
                {`Double gates (${enclosure.gateWidth}' wide opening)`}
              </span>
            </div>

            <div className="flex items-center text-sm">
              <svg
                className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-neutral-900">No center post between gates</span>
            </div>

            <div className="flex items-center text-sm">
              <svg
                className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-neutral-900">
                {`${enclosure.overheadClearance}' overhead clearance for truck access`}
              </span>
            </div>

            <div className="flex items-center text-sm">
              <svg
                className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-neutral-900">
                {`${enclosure.approachDistance}' unobstructed approach distance`}
              </span>
            </div>

            <div className="flex items-center text-sm">
              <svg
                className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-neutral-900">
                {`${enclosure.fireClearance}' clearance from buildings (fire code)`}
              </span>
            </div>
          </div>
        </div>

        {/* ADA Requirements */}
        <div className="mt-4 p-4 bg-neutral-50 rounded-lg border border-neutral-200">
          <h4 className="font-semibold text-neutral-900 mb-3">ADA Requirements</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-neutral-900">
                {`${accessibility.pathWidth}" minimum accessible path width`}
              </span>
            </div>
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-neutral-900">
                {`${accessibility.reachHeight}" maximum container reach height`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Why This Matters Section */}
      <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
        <h3 className="text-lg font-semibold text-primary-900 mb-3 flex items-center">
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Why This Matters
        </h3>
        <div className="text-sm text-primary-900 space-y-2">
          <p>
            California <strong>SB 1383</strong> requires all commercial properties to provide 3-stream
            collection (trash, recycling, and organics).
          </p>
          <p>
            {`Projects that don't plan adequate enclosure space often need `}<strong>costly retrofits of
            $15,000 or more</strong> to meet operational requirements.
          </p>
          <p>
            Planning proper dimensions and features now—especially the <strong>180° gate swing</strong>—saves
            money and ensures smooth operations from day one.
          </p>
        </div>
      </div>

      {/* AI Recommendations (if available) */}
      {requirements.recommendations && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 mb-3">Additional Recommendations</h3>
          <div className="prose prose-sm max-w-none text-neutral-700">
            <p className="whitespace-pre-wrap">{requirements.recommendations}</p>
          </div>
        </div>
      )}

      {/* CTA: Connect with Hauler */}
      <div className="pt-4 border-t border-neutral-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <h4 className="font-semibold text-neutral-900 mb-1">Next Step: Validate Requirements</h4>
            <p className="text-sm text-neutral-600">
              Contact local waste haulers to confirm these requirements for your specific location.
            </p>
          </div>
          <button
            onClick={handleContactHauler}
            className="btn-primary whitespace-nowrap"
            aria-label="Contact waste hauler about requirements"
          >
            <svg
              className="w-5 h-5 mr-2 inline"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Connect with Haulers
          </button>
        </div>
      </div>
    </div>
  );
}
